import { ACCENT } from '../catalog/accent';
import type { AccentKey } from '../catalog/accent';
import { CHANNEL_KIND, RCU_ACCEPTED_LOAD_TYPES } from '../catalog/channel';
import type { ChannelRef } from '../catalog/channel';
import { RCU_OUTPUT_RANGE } from '../catalog/limits';
import { LOAD_TYPE_SPECS } from '../catalog/loadType';
import type { AcceptedLoadTypes, LoadTypeKey } from '../catalog/loadType';
import { RAIL_TYPE_SPECS } from '../catalog/railType';
import type { DinRail, Load, RcuConfig, WiredLoad } from '../model';
import { channelCountAt, findRail, loadAtChannel } from './lookup';

/** Everything a list, badge or picker needs to describe one physical output. */
export interface ChannelTarget {
  readonly ref: ChannelRef;
  /** e.g. `RCU output 12` or `Phase cut @3 · channel 2`. */
  readonly label: string;
  /** Compact form for badges: `RCU CH 12` or `Phase cut @3 CH2`. */
  readonly shortLabel: string;
  readonly accent: AccentKey;
  readonly acceptedLoadTypes: AcceptedLoadTypes;
  /** What this output can do, e.g. `On / Off`. */
  readonly outputHint: string;
  /** The load wired here, if any. Wired by definition, so the channel is present. */
  readonly load: WiredLoad | undefined;
}

/** Channels on a rail: 1 up to the rail type's channel count. */
export const channelsOf = (rail: DinRail): readonly number[] =>
  Array.from({ length: RAIL_TYPE_SPECS[rail.type].channels }, (_unused, index) => index + 1);

export const rcuTarget = (config: RcuConfig, channel: number): ChannelTarget => {
  const ref: ChannelRef = { kind: CHANNEL_KIND.RCU, channel };
  return {
    ref,
    label: `RCU output ${channel}`,
    shortLabel: `RCU CH ${channel}`,
    accent: ACCENT.SLATE,
    acceptedLoadTypes: RCU_ACCEPTED_LOAD_TYPES,
    outputHint: LOAD_TYPE_SPECS[RCU_ACCEPTED_LOAD_TYPES[0]].outputHint,
    load: loadAtChannel(config, ref),
  };
};

export const dinTarget = (config: RcuConfig, rail: DinRail, channel: number): ChannelTarget => {
  const spec = RAIL_TYPE_SPECS[rail.type];
  const ref: ChannelRef = { kind: CHANNEL_KIND.DIN, railId: rail.id, channel };
  return {
    ref,
    label: `${spec.shortLabel} @${rail.address} · channel ${channel}`,
    shortLabel: `${spec.shortLabel} @${rail.address} CH${channel}`,
    accent: spec.accent,
    acceptedLoadTypes: spec.acceptedLoadTypes,
    outputHint: LOAD_TYPE_SPECS[spec.acceptedLoadTypes[0]].outputHint,
    load: loadAtChannel(config, ref),
  };
};

/** Onboard outputs in range, followed by every channel of every fitted rail. */
export const allTargets = (config: RcuConfig): readonly ChannelTarget[] => {
  const rcuTargets = RCU_OUTPUT_RANGE.filter(
    (channel) => channel <= config.rcu.outputCount,
  ).map((channel) => rcuTarget(config, channel));
  const railTargets = config.rails.flatMap((rail) =>
    channelsOf(rail).map((channel) => dinTarget(config, rail, channel)),
  );
  return [...rcuTargets, ...railTargets];
};

/** Resolves a channel reference to its target, or null when the rail is gone. */
export const targetFromRef = (config: RcuConfig, ref: ChannelRef): ChannelTarget | null => {
  if (ref.kind === CHANNEL_KIND.RCU) return rcuTarget(config, ref.channel);
  const rail = findRail(config, ref.railId);
  return rail === undefined ? null : dinTarget(config, rail, ref.channel);
};

/** Best-effort label for a reference, even one pointing at a deleted rail. */
export const describeRef = (config: RcuConfig, ref: ChannelRef): string => {
  const target = targetFromRef(config, ref);
  if (target !== null) return target.shortLabel;
  return ref.kind === CHANNEL_KIND.RCU ? `RCU CH ${ref.channel}` : `rail ${ref.railId} CH${ref.channel}`;
};

/**
 * The wiring address as a guest card shows it under the load's name: `RCU/12`
 * for an onboard output, `Phase cut @3 · ch 2` for a rail channel. Null when the
 * rail a channel points at has since been removed, so a card never claims an
 * address the configuration no longer holds.
 */
export const channelSubtext = (config: RcuConfig, ref: ChannelRef): string | null => {
  if (ref.kind === CHANNEL_KIND.RCU) return `RCU/${ref.channel}`;
  const rail = findRail(config, ref.railId);
  return rail === undefined
    ? null
    : `${RAIL_TYPE_SPECS[rail.type].shortLabel} @${rail.address} · ch ${ref.channel}`;
};

/** The output a wired load sits on, or null when its rail no longer exists. */
export const targetOfLoad = (config: RcuConfig, load: Load): ChannelTarget | null =>
  load.channel === null ? null : targetFromRef(config, load.channel);

/**
 * Free outputs, optionally narrowed to those that accept a load type. Used by
 * both assignment directions: pick a target for a load, or a load for a target.
 */
export const freeTargets = (
  config: RcuConfig,
  acceptedLoadType: LoadTypeKey | null,
): readonly ChannelTarget[] =>
  allTargets(config).filter(
    (target) =>
      target.load === undefined &&
      (acceptedLoadType === null || target.acceptedLoadTypes.includes(acceptedLoadType)),
  );

/** True when a channel number exists at the given target. */
export const isChannelInRange = (config: RcuConfig, ref: ChannelRef): boolean =>
  ref.channel >= 1 && ref.channel <= channelCountAt(config, ref);
